import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  generateBlogDraft,
  type BlogDraftLength,
  type BlogDraftTone,
  type GeneratedBlogDraft,
} from "@/services/blogService";

interface GenerateDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasExistingContent: boolean;
  hasExistingCover: boolean;
  onGenerated: (draft: GeneratedBlogDraft, opts: { autoCover: boolean }) => void;
}

export function GenerateDraftDialog({
  open,
  onOpenChange,
  hasExistingContent,
  hasExistingCover,
  onGenerated,
}: GenerateDraftDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState<BlogDraftTone>("informative");
  const [length, setLength] = useState<BlogDraftLength>("medium");
  const [audience, setAudience] = useState("");
  const [autoCover, setAutoCover] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const generateMutation = useMutation({
    mutationFn: () =>
      generateBlogDraft({
        topic: topic.trim(),
        tone,
        length,
        audience: audience.trim() || undefined,
      }),
    onSuccess: (draft) => {
      toast.success(t("blog.editor.ai.toast.draftReady"));
      onGenerated(draft, { autoCover: autoCover && !hasExistingCover });
      onOpenChange(false);
      setTopic("");
      setAudience("");
      setConfirmed(false);
    },
    onError: () => toast.error(t("blog.editor.ai.toast.draftError")),
  });

  const requiresConfirm = hasExistingContent && !confirmed;
  const canSubmit = topic.trim().length >= 3 && !generateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {t("blog.editor.ai.draftTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("blog.editor.ai.draftDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="ai-topic">
              {t("blog.editor.ai.fields.topic")}
            </Label>
            <Textarea
              id="ai-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("blog.editor.ai.fields.topicPlaceholder")}
              rows={3}
              maxLength={500}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ai-tone">{t("blog.editor.ai.fields.tone")}</Label>
              <Select
                value={tone}
                onValueChange={(v) => setTone(v as BlogDraftTone)}
              >
                <SelectTrigger id="ai-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="informative">
                    {t("blog.editor.ai.tones.informative")}
                  </SelectItem>
                  <SelectItem value="promotional">
                    {t("blog.editor.ai.tones.promotional")}
                  </SelectItem>
                  <SelectItem value="casual">
                    {t("blog.editor.ai.tones.casual")}
                  </SelectItem>
                  <SelectItem value="technical">
                    {t("blog.editor.ai.tones.technical")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-length">
                {t("blog.editor.ai.fields.length")}
              </Label>
              <Select
                value={length}
                onValueChange={(v) => setLength(v as BlogDraftLength)}
              >
                <SelectTrigger id="ai-length">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">
                    {t("blog.editor.ai.lengths.short")}
                  </SelectItem>
                  <SelectItem value="medium">
                    {t("blog.editor.ai.lengths.medium")}
                  </SelectItem>
                  <SelectItem value="long">
                    {t("blog.editor.ai.lengths.long")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="ai-audience">
              {t("blog.editor.ai.fields.audience")}
            </Label>
            <Input
              id="ai-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t("blog.editor.ai.fields.audiencePlaceholder")}
              maxLength={500}
            />
          </div>

          {!hasExistingCover && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
              <input
                type="checkbox"
                checked={autoCover}
                onChange={(e) => setAutoCover(e.target.checked)}
                className="mt-0.5"
              />
              <span>{t("blog.editor.ai.autoCoverLabel")}</span>
            </label>
          )}

          {hasExistingContent && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/60 dark:bg-amber-950/30">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-amber-900 dark:text-amber-100">
                {t("blog.editor.ai.overwriteWarning")}
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generateMutation.isPending}
          >
            {t("blog.editor.ai.actions.cancel")}
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={!canSubmit || requiresConfirm}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {generateMutation.isPending
              ? t("blog.editor.ai.actions.generating")
              : t("blog.editor.ai.actions.generate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
